// @refresh reload
import "solid-devtools/setup";

import Log from "@repo/utils/Log";
import Env from "@repo/utils-client/Env";
import Platform from "@repo/utils-client/Platform";
Env.debug = Log.enabled =
  Platform.isClient &&
  (import.meta.env.APP_ENV !== "production" ||
    new URLSearchParams(window.location.search).has("DEBUG"));

import {
  ComponentProps,
  ErrorBoundary,
  getOwner,
  JSX,
  onMount,
} from "solid-js";
import { createEffect, Suspense } from "solid-js";
import { MetaProvider, Title } from "@solidjs/meta";
import { Router, RouteSectionProps } from "@solidjs/router";
import Cookies from "js-cookie";
import { captureException } from "@sentry/solid";

import t from "@repo/localization/string.macro";
import LoadableButton from "@repo/ui/buttons/LoadableButton";
import ShowMore from "@repo/ui/containers/ShowMore";
import { useError } from "@repo/ui/error/ErrorProvider";
import GoogleMapsSolid from "@repo/ui/external/GoogleMapsSolid";
import Stripe from "@repo/ui/external/Stripe";
import GlobalRoot from "@repo/ui/global/GlobalRoot";
import { invalidate } from "@repo/ui/global/signals";
import Constraints from "@repo/ui/layout/Constraints";
import Spinner from "@repo/ui/loaders/Spinner";
import Locale from "@repo/utils/Locale";
import UArray from "@repo/utils/UArray";
import UBoolean from "@repo/utils/UBoolean";
import UIterable from "@repo/utils/UIterable";
import AppRouter from "@repo/utils-solid/AppRouter";

import analytics, { track } from "~/analytics/client";
import appSettings from "~/appSettings";
import { OverlaysProvider } from "~/overlays";
import { shouldServeUsMigrationRoute } from "~/routes/api/us/available-routes";
import { features } from "~/state";
import { useUser } from "~/state/features/user";

declare namespace SharedRootRouter {
  type Props = {
    root?: (props: RouteSectionProps) => JSX.Element;
    children: ComponentProps<typeof Router>["children"];
  };
}

function SharedRootRouter({ root, children }: D<SharedRootRouter.Props>) {
  onMount(() => {
    const locale =
      UIterable.firstDerived(
        [
          Cookies.get("locale"),
          document.referrer &&
            { "/": "en", "/fr-fr": "fr" }[new URL(document.referrer).pathname],
          ...navigator.languages,
        ].filter(UBoolean.isTruthy),
        (language) => {
          const locale = language.slice(0, 2);
          if (UArray.includesLiteral(appSettings.locales, locale))
            return locale;
        }
      ) || appSettings.locales[0];

    if (locale !== Locale.current) {
      Locale.current = locale;
      invalidate();
    }
  });

  return (
    <ErrorBoundary
      fallback={(e) => {
        // TODO: Router currently bugs out when logging out (invalidating), then
        // routing to 404 page, reload when this happens. Need to submit github
        // issue with reproduction
        if (e.message.includes("path") || e.message.includes("match")) {
          onMount(() => {
            // Reloading immediately loads old path, must delay
            setTimeout(() => {
              window.location.reload();
            });
          });

          return <Spinner.Common />;
        }

        captureException(e);
        throw e;
      }}
    >
      <ShowMore.PropsProvider
        text={(show) => (show ? t`Show less` : t`Show more`)}
      >
        <LoadableButton.PropsProvider
          text={{
            pending: t`Loading...`,
            success: t`Success`,
          }}
        >
          <Constraints.PropsProvider maxWidth="1000px">
            <Stripe.Provider
              key={import.meta.env.STRIPE_PUBLIC_KEY}
              optionsFor={{
                payment: {
                  defaultValues: {
                    billingDetails: {
                      address: {
                        country: appSettings.country,
                      },
                    },
                  },
                },
              }}
            >
              <GoogleMapsSolid.Provider
                key={import.meta.env.GOOGLE_MAPS_API_KEY}
                country={appSettings.country}
              >
                <MetaProvider>
                  <Title>
                    {import.meta.env.APP_ENV === "development"
                      ? (
                          { canada: "🇨🇦 ", us: "🇺🇸 " } as Record<string, string>
                        )[import.meta.env.API_ADAPTER]
                      : ""}
                    Kikoff
                  </Title>

                  <Router
                    root={(props) => {
                      AppRouter.register({
                        external: (path) =>
                          import.meta.env.API_ADAPTER === "us" &&
                          !shouldServeUsMigrationRoute(path),
                      });

                      createEffect((from?: string) => {
                        track.page("impression", {
                          initial: !from,
                          from: from!,
                        });
                        return AppRouter.pathname;
                      });

                      return (
                        <Suspense>
                          {/* GlobalRoot must be inside of Router since overlays depend on Route context  */}
                          <GlobalRoot
                            OverlaysProvider={OverlaysProvider}
                            featureProviders={features.map(
                              ({ Provider }) => Provider
                            )}
                            devtools={{
                              featureContexts: features.map(
                                ({ Context }) => Context
                              ),
                            }}
                          >
                            {(() => {
                              const [, setUser] = useUser();
                              const error = useError();

                              analytics.context = { owner: getOwner()! };

                              onMount(() => {
                                setUser.init().catch((e) => {
                                  error.capture(e);
                                });
                              });

                              return <>{root?.(props) || props.children}</>;
                            })()}
                          </GlobalRoot>
                        </Suspense>
                      );
                    }}
                  >
                    {children}
                  </Router>
                </MetaProvider>
              </GoogleMapsSolid.Provider>
            </Stripe.Provider>
          </Constraints.PropsProvider>
        </LoadableButton.PropsProvider>
      </ShowMore.PropsProvider>
    </ErrorBoundary>
  );
}

export default SharedRootRouter;
